/**
 * Right-anchored drawer hosting the Watchlist-parity filter panel for the
 * pipeline TABLE view (SE-7144).
 *
 * Deliberately a THIN shell: `AddPlayersFilterPanel` already renders the
 * complete panel — the FILTERS + CLEAR ALL header, every Watchlist section
 * (BASIC with Roles / Positions / Leagues / Clubs / Nationalities / Age /
 * Months Remaining, FINANCIAL, FIT & RATINGS, PERFORMANCE, GOALKEEPER, plus
 * LISTS), and the Cancel / Show results footer — so this component only
 * supplies the same drawer chrome the board's other panels use. One panel
 * implementation therefore serves the add-players drawer AND the table, and
 * both track the Watchlist by construction.
 * @module features/player-kanban/components/organisms/TableFilterDrawer/TableFilterDrawerView
 */

import React, { memo } from "react";

import {
  Drawer,
  DrawerBackdrop,
  DrawerContent,
} from "@/components/atoms/Drawer";

import AddPlayersFilterPanel, {
  type AddPlayersFilterPanelContainerProps,
} from "../AddPlayersFilterPanel";

/** Props for {@link TableFilterDrawerView}. */
export interface TableFilterDrawerProps extends Omit<
  AddPlayersFilterPanelContainerProps,
  "reasonSelection" | "reasonOptions" | "onCommitReasons"
> {
  /** Whether the drawer is open. */
  readonly isOpen: boolean;
}

/** The table has no suggestion candidates, so REASON never renders. */
const NO_REASONS: readonly never[] = [];

/** REASON commits are impossible with no options; the panel still calls it. */
const noOpReasons = (): void => undefined;

/**
 * The Table view's FILTERS drawer.
 * @param props - Drawer visibility plus the panel's own contract.
 * @param props.isOpen - Whether the drawer is open.
 * @param props.onClose - Closes the drawer (Cancel and post-commit both land here).
 * @returns The drawer element.
 */
const TableFilterDrawerView = ({
  isOpen,
  onClose,
  ...panelProps
}: TableFilterDrawerProps) => (
  <Drawer isOpen={isOpen} onClose={onClose} anchor="right" size="sm">
    <DrawerBackdrop />
    <DrawerContent className="!overflow-hidden !p-0">
      {isOpen ? (
        <AddPlayersFilterPanel
          {...panelProps}
          reasonSelection={NO_REASONS}
          reasonOptions={NO_REASONS}
          onCommitReasons={noOpReasons}
          onClose={onClose}
        />
      ) : null}
    </DrawerContent>
  </Drawer>
);

TableFilterDrawerView.displayName = "TableFilterDrawerView";

export default memo(TableFilterDrawerView);
